import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_TASKS = [
  { title: 'Complete Personal Information Form', description: 'Fill out all required personal details', order: 1, is_required: true, requires_document: false },
  { title: 'Submit ID Proof', description: 'Upload government-issued ID (Aadhar, PAN, Passport)', order: 2, is_required: true, requires_document: true, document_type: 'ID Proof' },
  { title: 'Submit Address Proof', description: 'Upload address proof document', order: 3, is_required: true, requires_document: true, document_type: 'Address Proof' },
  { title: 'Submit Education Certificates', description: 'Upload all educational certificates', order: 4, is_required: true, requires_document: true, document_type: 'Education Certificate' },
  { title: 'Bank Account Details', description: 'Provide bank account information for salary', order: 5, is_required: true, requires_document: false },
  { title: 'IT Setup - Email & System Access', description: 'Complete IT setup and get system access', order: 6, is_required: true, requires_document: false },
  { title: 'Team Introduction Meeting', description: 'Meet with team members and manager', order: 7, is_required: true, requires_document: false },
  { title: 'Company Policies Review', description: 'Read and acknowledge company policies', order: 8, is_required: true, requires_document: false },
  { title: 'Office Tour', description: 'Complete office tour and facility orientation', order: 9, is_required: false, requires_document: false },
  { title: 'Emergency Contact Information', description: 'Provide emergency contact details', order: 10, is_required: true, requires_document: false }
];

function Onboarding({ user, onLogout }) {
  const [workflows, setWorkflows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    start_date: new Date().toISOString().split('T')[0],
    expected_completion_date: '',
    tasks: DEFAULT_TASKS.map(t => ({ ...t, status: 'Not Started' }))
  });

  useEffect(() => {
    fetchWorkflows();
    fetchDashboardStats();
    if (user.role === 'Admin' || user.role === 'HR Manager') {
      fetchEmployees();
    }
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(emp => emp.employee_status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Onboarding workflow created successfully!');
      setShowForm(false);
      resetForm();
      fetchWorkflows();
    } catch (error) {
      alert('Failed to create workflow: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTaskUpdate = async (workflowId, taskId, status, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/onboarding/${workflowId}/task/${taskId}`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkflows();
    } catch (error) {
      alert('Failed to update task: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this onboarding workflow?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/onboarding/${workflowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Workflow deleted successfully!');
      fetchWorkflows();
    } catch (error) {
      alert('Failed to delete workflow: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      start_date: new Date().toISOString().split('T')[0],
      expected_completion_date: '',
      tasks: DEFAULT_TASKS.map(t => ({ ...t, status: 'Not Started' }))
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'bg-gray-100 text-gray-700',
      'In Progress': 'bg-[#2b338030] text-blue-700',
      'Completed': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getProgressColor = (progress) => {
    if (progress === 100) return 'bg-green-500';
    if (progress >= 50) return 'bg-[#2b338015]0';
    if (progress > 0) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Employee Onboarding</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button
              onClick={() => { setShowForm(true); resetForm(); }}
              className="px-6 py-2 rounded-lg text-white hover:opacity-90"
              style={{backgroundColor: '#2b3380'}}
            >
              Create Onboarding Workflow
            </button>
          )}
        </div>

        {/* Onboarding Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Onboarding Overview"
            icon="🎯"
            bgColor="#009688"
            stats={[
              {
                label: 'Pending Onboarding',
                value: dashboardStats.pending_onboarding || 0,
                subtitle: 'In progress',
                color: '#ff9800'
              },
              {
                label: 'Completed',
                value: dashboardStats.completed_onboarding || 0,
                subtitle: 'Successfully done',
                color: '#4caf50'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Active staff',
                color: '#2b3380'
              },
              {
                label: 'Default Tasks',
                value: '10',
                subtitle: 'Checklist items',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Create Workflow Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Create Onboarding Workflow</h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">New Employee *</label>
                    <select
                      value={formData.employee_id}
                      onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                      required
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Start Date *</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Expected Completion Date</label>
                      <input
                        type="date"
                        value={formData.expected_completion_date}
                        onChange={(e) => setFormData({...formData, expected_completion_date: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Onboarding Tasks</label>
                    <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto">
                      {formData.tasks.map((task, index) => (
                        <div key={index} className="flex items-center mb-2 text-sm">
                          <span className="w-8 text-gray-500">{task.order}.</span>
                          <span className="flex-1">{task.title}</span>
                          {task.requires_document && (
                            <span className="text-xs bg-[#2b338030] text-blue-700 px-2 py-1 rounded">
                              📄 Doc Required
                            </span>
                          )}
                          {task.is_required && (
                            <span className="text-xs text-red-600 ml-2">*</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      Create Workflow
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Workflow Details Modal */}
        {selectedWorkflow && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                      Onboarding Progress - {selectedWorkflow.employee_name}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Started: {formatDate(selectedWorkflow.start_date)}
                      {selectedWorkflow.expected_completion_date && (
                        <> • Expected: {formatDate(selectedWorkflow.expected_completion_date)}</>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedWorkflow(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Overall Progress</span>
                    <span className="text-sm font-bold">{selectedWorkflow.progress_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className={`h-4 rounded-full transition-all ${getProgressColor(selectedWorkflow.progress_percentage)}`}
                      style={{width: `${selectedWorkflow.progress_percentage}%`}}
                    ></div>
                  </div>
                  <div className="mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedWorkflow.overall_status)}`}>
                      {selectedWorkflow.overall_status}
                    </span>
                  </div>
                </div>

                {/* Task Checklist */}
                <div className="space-y-3">
                  {selectedWorkflow.tasks?.map((task, index) => (
                    <div
                      key={task.id}
                      className={`border rounded-lg p-4 ${task.status === 'Completed' ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={task.status === 'Completed'}
                            onChange={(e) => {
                              const newStatus = e.target.checked ? 'Completed' : 'Not Started';
                              handleTaskUpdate(selectedWorkflow.id, task.id, newStatus);
                            }}
                            className="w-5 h-5 rounded"
                            style={{accentColor: '#2b3380'}}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{task.title}</span>
                            {task.is_required && <span className="text-red-600 text-sm">*</span>}
                            {task.requires_document && (
                              <span className="text-xs bg-[#2b338030] text-blue-700 px-2 py-1 rounded">
                                📄 {task.document_type}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                          {task.notes && (
                            <p className="text-sm text-gray-500 mt-2 italic">Notes: {task.notes}</p>
                          )}
                          {task.completed_at && (
                            <p className="text-xs text-green-600 mt-2">
                              ✓ Completed on {formatDate(task.completed_at)}
                            </p>
                          )}
                        </div>
                        <div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workflows List */}
        <div className="space-y-4">
          {workflows.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              <p className="text-lg">No onboarding workflows found.</p>
              {(user.role === 'Admin' || user.role === 'HR Manager') && (
                <p className="mt-2">Create a workflow to get started with employee onboarding.</p>
              )}
            </div>
          ) : (
            workflows.map((workflow) => (
              <div
                key={workflow.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>
                        {workflow.employee_name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(workflow.overall_status)}`}>
                        {workflow.overall_status}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-3">
                      <span>Started: {formatDate(workflow.start_date)}</span>
                      {workflow.expected_completion_date && (
                        <span className="ml-4">Expected: {formatDate(workflow.expected_completion_date)}</span>
                      )}
                      {workflow.actual_completion_date && (
                        <span className="ml-4 text-green-600">✓ Completed: {formatDate(workflow.actual_completion_date)}</span>
                      )}
                    </div>

                    <div className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Progress</span>
                        <span className="text-sm font-bold">{workflow.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${getProgressColor(workflow.progress_percentage)}`}
                          style={{width: `${workflow.progress_percentage}%`}}
                        ></div>
                      </div>
                    </div>

                    <div className="text-sm text-gray-500">
                      <span>Assigned by: {workflow.assigned_by_name}</span>
                      <span className="ml-4">Tasks: {workflow.tasks?.length || 0}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => setSelectedWorkflow(workflow)}
                      className="px-4 py-2 rounded-lg text-white hover:opacity-90"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      View Details
                    </button>
                    {(user.role === 'Admin' || user.role === 'HR Manager') && (
                      <button
                        onClick={() => handleDelete(workflow.id)}
                        className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Onboarding;
